/**
 * batchManagementService -- the ONLY place the batch UI knows the wire
 * format of the batch-management API.
 *
 * WHY A SEPARATE SERVICE FROM batchService.js: batchService.js talks to the
 * legacy `/batch/*` endpoints, which are manual 2021-OW grouping (create a
 * batch by hand, unbatch selected orders). The design doc is explicit that
 * the automatic lifecycle must NOT be wired into that flow
 * (docs/planning/2026-08-18-order-batching-implementation-design.md 3.1:
 * "Do NOT wire the legacy CreateBatchDialog flow into the auto lifecycle").
 * Two mechanisms for two different concepts, kept apart deliberately.
 *
 * FIELD NAMES ARE NOT INVENTED. Every backend field below comes from
 * docs/planning/2026-08-18-order-batching-implementation-design.md 3.1-3.4:
 *
 *   batches.state                        open | holding | released | failed
 *   batches.blanks_cost_cents            running wholesale total
 *   batches.opened_at / first_order_at / holding_since / released_at
 *   batches.release_rule_fired           time | dollar | store_close | manual
 *   stores.batch_hold_hours              NULL = inherit platform default (48)
 *   stores.batch_blanks_threshold_cents  NULL = inherit platform default
 *   stores.shopper_editing_enabled       NOT NULL, defaults false
 *   orders.payment_state                 pending_capture | charged |
 *                                        charge_failed | canceled
 *   orders.last_charge_error             surfaced to the distributor
 *
 * Note the prototype used `status`; the backend column is `state`. The
 * normalisation happens here so no component has to know both names.
 *
 * The endpoint PATHS (/batch-management/...) and the two response keys
 * `held_back_moved_to_batch_id` and `release_mode` are the assumptions that
 * a parallel worker's API must be reconciled against -- they are listed in
 * the PR body. Everything else is quoted schema.
 */
import api from "./api";

const BASE = "/batch-management";

// ---------------------------------------------------------------------------
// Money. Cents on the wire, dollars in the UI, whole cents always.
// ---------------------------------------------------------------------------

export const centsToDollars = (c) => Math.round(Number(c) || 0) / 100;
export const dollarsToCents = (d) => Math.round(Number(d) * 100) || 0;

export function money(n) {
  return (
    "$" +
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// ---------------------------------------------------------------------------
// Time. Instants on the wire, short labels in the UI.
//
// The batch and order timestamp columns are TIMESTAMP WITHOUT TIME ZONE,
// written as UTC (`now() AT TIME ZONE 'UTC'`, `TIMEZONE('UTC', NOW())`), so
// the API's isoformat() carries NO offset. Date.parse reads an offset-less
// date-time in the VIEWER's zone, which would shift every time by the
// viewer's UTC offset -- so an offset-less value is read as UTC here. A value
// that does carry an offset (`releases_at` is offset-aware) is read as-is.
//
// Displayed on the viewer's clock, like the order detail page's "Placed".
// ---------------------------------------------------------------------------

const HAS_OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/i;

export function parseInstant(value) {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  const d = new Date(HAS_OFFSET_RE.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Oct 7, 9:12 AM" -- the year only when it is not this year. "" if absent. */
export function formatWhen(value, now = new Date()) {
  const d = parseInstant(value);
  if (!d) return "";
  const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleString("en-US", opts);
}

/** "2d 3h", "21h 47m", "12m", "<1m"; "0m" for a span already over. */
export function formatDuration(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "0m";
  const totalMin = Math.floor(n / 60000);
  if (totalMin < 1) return "<1m";
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function durationUntil(value, now = new Date()) {
  const d = parseInstant(value);
  return d ? formatDuration(d.getTime() - now.getTime()) : "";
}

function durationSince(value, now = new Date()) {
  const d = parseInstant(value);
  return d ? formatDuration(now.getTime() - d.getTime()) : "";
}

// ---------------------------------------------------------------------------
// Decorator routing. Which decorator(s) a batch went to, as the API worked
// it out (`_decorator_routing_by_batch`): `named` (one we can name),
// `multiple` (orders are with `count` decorators), `automatic` (the store
// sends no decorator; the back office assigns one), `unknown` (nothing we
// can name). The UI only words it -- the precedence lives server-side.
// ---------------------------------------------------------------------------

const UNKNOWN_ROUTING = Object.freeze({ kind: "unknown", names: [], count: 0 });

function normalizeRouting(raw, legacyName) {
  if (raw && typeof raw === "object" && raw.kind) {
    const names = Array.isArray(raw.names) ? raw.names.filter(Boolean) : [];
    return { kind: raw.kind, names, count: Number(raw.count) || names.length };
  }
  // An API build that predates routing still sends `decorator_name`, and the
  // design fixture a bare `decorator` string: one name is a named routing.
  if (legacyName) return { kind: "named", names: [legacyName], count: 1 };
  return { ...UNKNOWN_ROUTING };
}

/** The released row's clause: "Sent to ...", or "" when there is none. */
export function decoratorSentLine(routing) {
  if (!routing) return "";
  if (routing.kind === "named" && routing.names && routing.names[0]) {
    return `Sent to ${routing.names[0]}`;
  }
  if (routing.kind === "multiple" && routing.count > 1) {
    return `Sent to ${routing.count} decorators`;
  }
  if (routing.kind === "automatic") {
    return "Sent to the back office, decorator assigned automatically";
  }
  return "";
}

/**
 * Who a sentence sends to or tells you to contact: "Release 4 orders to
 * <target>". Never "" -- these are mid-sentence, so unknown falls back to
 * the generic "the decorator" the Released chip already uses.
 */
export function decoratorTarget(routing) {
  if (routing && routing.kind === "automatic") return "the back office";
  return decoratorLabel(routing) || "the decorator";
}

/** The short form, for the Decorator stat and the review: "" when unknown. */
export function decoratorLabel(routing) {
  if (!routing) return "";
  if (routing.kind === "named" && routing.names && routing.names[0]) {
    return routing.names[0];
  }
  if (routing.kind === "multiple" && routing.count > 1) {
    return `${routing.count} decorators`;
  }
  if (routing.kind === "automatic") return "Assigned automatically";
  return "";
}

// ---------------------------------------------------------------------------
// The size list used by the DEMO FIXTURES only (`makeFixtureBatches`). A real
// order's sizes come off its own line (`availableSizes`), served per line from
// the campaign product -- never from a module constant, because a constant
// cannot know what the product is and gave an OSFA cap eight size chips.
//
// THERE IS NO CLIENT-SIDE UPCHARGE TABLE, and there must never be one again
// (GH #3741). It used to live here as `SIZE_UPCHARGE` (2XL +$3, 3XL +$5,
// 4XL +$7) and the design bundle's "sizes show their upcharge on the chip"
// is what put it there. The numbers corresponded to nothing: the real
// per-size cost lives in `added_cost_and_margin` behind a four-level margin
// hierarchy, and the server already resolves it into the price it ships.
// Re-deriving it here would be, in `_build_sizes`' own words, the SIXTH
// reimplementation of that chain -- and it drifted from the charge, which is
// the only number that matters.
// ---------------------------------------------------------------------------

export const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];

export const STYLES = Object.freeze([
  { id: "p1", name: "Port Authority K500 Silk Touch Polo", base: 28.0, colors: ["Navy", "Black", "White", "Steel Grey", "Royal"] },
  { id: "p2", name: "Nike Dri-FIT Micro Pique Polo", base: 42.0, colors: ["Navy", "Black", "White", "Gym Red"] },
  { id: "p3", name: "District Perfect Tri Tee", base: 18.5, colors: ["Navy Frost", "Black Frost", "White", "Grey Frost"] },
  { id: "p4", name: "Carhartt Midweight Hooded Sweatshirt", base: 62.0, colors: ["Navy", "Black", "Carhartt Brown"] },
]);

export const COLOR_HEX = Object.freeze({
  Navy: "#1f2f4d",
  Black: "#1c1c1c",
  White: "#f4f4f2",
  "Steel Grey": "#8d949c",
  Royal: "#1c4fd8",
  "Gym Red": "#b31217",
  "Navy Frost": "#31425f",
  "Black Frost": "#3a3a3a",
  "Grey Frost": "#9aa0a6",
  "Carhartt Brown": "#69492b",
});

// ---------------------------------------------------------------------------
// Platform defaults. Design doc 3.3: platform_settings carries
// batch_default_hold_hours = 48 and batch_default_blanks_threshold_cents =
// 20100. A NULL on the store row means "inherit these".
// ---------------------------------------------------------------------------

export const DEFAULT_RULES = Object.freeze({
  windowHours: 48,
  threshold: 201,
  releaseMode: "manual",
  shopperEditing: false,
  storeType: "standing",
  // Both rules ON is the shipped behaviour and the column default. A store
  // turns one off deliberately; nothing should default to off.
  timeRuleEnabled: true,
  dollarRuleEnabled: true,
});

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

/**
 * What one line is worth: THE WIRE PRICE, times quantity. Nothing else.
 *
 * `base` is the server's `unit_price_cents` (see `normalizeOrder`), which is
 * the price the card is actually charged -- `_build_sizes` resolves it
 * through `compute_order_item_price_snapshot`, the one function that writes
 * `order_items.unit_price` at checkout. The size upcharge is ALREADY IN IT.
 *
 * This used to add a client-side `SIZE_UPCHARGE` table on top, which showed
 * a distributor more money than the order would charge -- $19.99 against a
 * stored $14.99 on order 8 (GH #3741). It was silent, because the two
 * numbers only disagree on 2XL/3XL/4XL lines. Do not reintroduce an
 * adjustment here: any arithmetic the client does to a served price is a
 * second opinion about money the server has already decided.
 */
export const lineTotal = (l) => Number(l.base || 0) * Number(l.qty || 0);

/** Gross value of the goods, before any discount. */
export const orderSubTotal = (o) =>
  (o.lines || []).reduce((s, l) => s + lineTotal(l), 0);

// ---------------------------------------------------------------------------
// Highest-priced-item-free (https://trello.com/c/T756dpsG).
//
// FROZEN, NOT RECOMPUTED. `order_items.free_item_discount_cents` is written
// once at checkout and the batch charge reads exactly that column
// (`account._frozen_free_item_discount_cents` -- "never re-derives the rule",
// so an order's charge cannot change because the store's configuration did).
// `batch_order_edit.EDITABLE_ITEM_FIELDS` is quantity/colour/size/style only,
// so editing a line leaves the discount untouched on the server. Mirroring
// that here is what keeps the editor's total equal to what will actually be
// charged; recomputing client-side would print a different number.
//
// Per LINE, because that is how it is stored -- one unit of one line is free,
// so the amount does not scale with quantity.
// ---------------------------------------------------------------------------

export const lineDiscount = (l) => Number(l.discount || 0);

export const orderDiscount = (o) =>
  (o.lines || []).reduce((s, l) => s + lineDiscount(l), 0);

/**
 * What the order is actually worth: goods less the frozen discount.
 *
 * Floored at zero. A discount can never exceed the line it came from, but a
 * corrupt row must not print a negative order total to a distributor.
 */
export const orderTotal = (o) =>
  Math.max(0, orderSubTotal(o) - orderDiscount(o));

export const itemCount = (o) =>
  (o.lines || []).reduce((s, l) => s + Number(l.qty || 0), 0);

// ---------------------------------------------------------------------------
// Type-down filtering over a batch's orders
// ---------------------------------------------------------------------------

/**
 * Does this LINE match a filter term? (style name / SKU / style id)
 *
 * Lives here, beside `orderMatchesFilter`, so the list's "which orders show"
 * and the row's "which lines are highlighted" cannot drift into disagreeing.
 * A highlight that marks a line the filter did not actually match is worse
 * than no highlight at all -- it asserts a reason that is not the reason.
 */
export function lineMatchesFilter(line, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return false;
  return [line.style, line.sku, line.styleId]
    .filter((v) => v != null && v !== "")
    .some((v) => String(v).toLowerCase().includes(q));
}

/**
 * Does this ORDER match a filter term?
 *
 * Jake, 2026-09-22: "filter orders in a batch by type-down search on name,
 * style number, or email address." Those three fields, plus the order's
 * number (what a distributor reads off an email thread) and its "O-<pk>"
 * display id.
 *
 * SUBSTRING, not prefix: type-down means a distributor types the part they
 * remember, which is often mid-word ("opper" for Hopper).
 *
 * An EMPTY term matches everything. The caller renders the unfiltered list
 * rather than an empty one -- "I have not typed anything" is not "nothing
 * matched".
 */
export function orderMatchesFilter(order, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return true;

  const fields = [order.shopper, order.shopperEmail, order.orderNumber, order.id, order.dept];
  if (fields.some((v) => v && String(v).toLowerCase().includes(q))) return true;

  return (order.lines || []).some((l) => lineMatchesFilter(l, q));
}

// A decorator-cancelled order IS counted in both totals below: it stays in
// its batch, was charged, and is not refunded automatically, so the batch's
// value and charged total include it (its row carries the owner's badge).
// The batch's card charge, from the server's per-order figure. NULL unless
// EVERY non-held order has one: a partial sum would read as a total.
export const batchChargeTotal = (b) => {
  const live = (b.orders || []).filter((o) => !o.held);
  if (live.some((o) => o.cardCharge == null)) return null;
  return live.reduce((s, o) => s + o.cardCharge, 0);
};

export const batchTotal = (b) =>
  (b.orders || []).reduce((s, o) => s + orderTotal(o), 0);

// A held-back order was never charged, so it is not part of the charged total.
export const chargedTotal = (b) =>
  (b.orders || []).filter((o) => !o.held).reduce((s, o) => s + orderTotal(o), 0);

// ---------------------------------------------------------------------------
// Normalisation -- backend shape in, UI shape out.
// ---------------------------------------------------------------------------

const displayId = (prefix, id) => (id == null ? null : `${prefix}-${id}`);

/**
 * The primary key to address on the wire.
 *
 * TAKES A NORMALIZED BATCH/ORDER OBJECT BY PREFERENCE, and reads `rawId` off
 * it -- the pk straight from the API, the one field here that cannot be
 * wrong. It also still accepts a bare number or a "B-<pk>" display string,
 * because the design fixtures and a few callers pass those.
 *
 * WHY IT CANNOT JUST PARSE THE DISPLAY ID, which is what it used to do:
 * `normalizeBatch` resolves the display id as `raw.name || raw.display_id ||
 * "B-<id>"`, and THE SEEDER NAMES EVERY BATCH IT CREATES. So on any seeded
 * sandbox the id is "seed-1-holding", which has no trailing digits --
 * trailing-digit scraping returned null and every write path POSTed to
 * `/batch-management/batches/null/...` and took a 405. Found on a seeded
 * 8086 by the unit N ui-runner scenario, 2026-09-16; the entire existing
 * unit suite missed it because `makeFixtureBatches` uses the "B-<pk>" form
 * exclusively, which is the one shape the broken parse handled.
 *
 * The name is NOT parsed as a fallback, deliberately. "seed-1-holding"
 * contains a 1, and a looser regex would return batch 1 -- a real but
 * DIFFERENT batch. Addressing the wrong row is worse than failing to
 * address one, especially on a path that charges cards. Null is the honest
 * answer for a bare name, and the caller passes the object instead.
 */
export function toNumericId(id) {
  if (typeof id === "number") return id;
  // A normalized batch or order. `rawId` is the pk off the wire.
  if (id && typeof id === "object" && id.rawId != null) return Number(id.rawId);
  const m = String(id || "").match(/^[A-Za-z]-(\d+)$/);
  return m ? Number(m[1]) : null;
}

export function normalizeOrder(raw = {}) {
  // Held back == the charge was attempted and declined. `canceled` is not
  // held back: the distributor removed it deliberately.
  const held = raw.payment_state === "charge_failed";
  return {
    // `id` is the row's IDENTITY (replace/remove matching, testids,
    // `toNumericId`), not what a human reads. The API sends no `display_id`,
    // so this is always "O-<pk>".
    id: raw.display_id || displayId("O", raw.id),
    rawId: raw.id,
    // What a human READS: the store's configured order number (e.g.
    // "AC-2026-02284"), the same string emails, the portal and the CSV
    // print. "O-<pk>" only for a numberless legacy row -- the rule
    // `display_order_number` applies in the email layer. Same pair of
    // fields `normalizeReleaseItem` carries.
    orderNumber: raw.order_number || "",
    orderLabel: raw.order_number || displayId("O", raw.id),
    shopper: raw.shopper_name || raw.shopper || "",
    // The address the system will actually mail: the serializer reads the
    // same `checkout_fields["Company Email"]` key the mail layer sends to
    // (Jake, 2026-09-22 -- the distributor needs to be able to reach the
    // shopper while the order is still fixable). "" when the order carries
    // none, which is a real state: checkout_fields is nullable and
    // unconstrained, so one empty shape keeps callers from branching on two.
    shopperEmail: raw.shopper_email || raw.shopperEmail || "",
    // WHERE the order is going. The FROZEN ship-to the backend resolved at
    // checkout, passed through as-is rather than flattened to a string here:
    // the component decides how to lay an address out, and a pre-joined
    // string would force it to re-split one. `null` when the order has no
    // address -- the serializer sends null rather than a husk of empty
    // values precisely so there is ONE thing to branch on, and an absent
    // address is the common case (7 of 276 orders on 8083 carry one).
    shipTo: raw.ship_to || raw.shipTo || null,
    dept: raw.department || raw.dept || "",
    // The decorator that reported on THIS order, "" until one has. Shown on
    // the row only when the batch went to several decorators.
    decoratorName: raw.decorator_name || "",
    // The decorator cancelled this order (back office; Jake). The batch
    // endpoints are owner-side, so `status` arrives unmasked. Shown as a
    // badge on the row; the order stays in its released batch.
    decoratorCancelled: raw.status === "decorator_cancelled",
    // The API sends the instant (`placed_at`, the order's created_at);
    // `placed` is the design fixture's pre-formatted spelling.
    placed: formatWhen(raw.placed_at) || raw.placed || "",
    // What release charges this order's saved card, as the SERVER computed it
    // (`card_charge_cents`: the charger's own helper on the order's frozen
    // columns). Dollars here like every other money value. NULL = the server
    // could not price it: unknown, never 0 and never the apparel value
    // (#630). 0 is a real value (payroll / Net Checkout / allowance-covered).
    cardCharge:
      raw.card_charge_cents != null ? centsToDollars(raw.card_charge_cents) : null,
    paymentState: raw.payment_state || "pending_capture",
    // False when the payload carried no payment_state and the line above
    // defaulted it: an unknown state must never be read as "not charged".
    paymentStateKnown: Boolean(raw.payment_state),
    // Whether Cancel is offered, as the SERVER decided it
    // (`batch_order_edit.can_cancel_order`): the UI never re-derives it from
    // paymentState. Absent means offered, so an older payload or a design
    // fixture keeps its button; the endpoint is the guard either way.
    canCancel: raw.can_cancel !== false,
    held,
    reason: held ? raw.last_charge_error || "Card declined" : undefined,
    movedTo: displayId("B", raw.moved_to_batch_id) || undefined,
    lines: (raw.lines || []).map((l) => ({
      // The order_items row id. LOAD-BEARING, not cosmetic: the PATCH
      // handler verifies every id belongs to this order and refuses a line
      // without one ("each line needs an integer id"), so an id dropped
      // here makes every save a 400.
      id: l.id != null ? l.id : null,
      styleId: l.style_id || l.styleId,
      style: l.style_name || l.style,
      base: l.unit_price_cents != null ? centsToDollars(l.unit_price_cents) : Number(l.base || 0),
      color: l.color,
      size: l.size,
      qty: Number(l.quantity != null ? l.quantity : l.qty) || 0,
      decoration: l.decoration_label || l.decoration || "",
      // WHICH GARMENT this line is, and what it may be changed to -- served
      // per line by `_product_data_for_lines`. Null / empty when the product
      // does not carry it, NEVER a fallback catalog: rendering an invented
      // garment on a real order is what this replaces, and an OSFA cap got
      // eight size chips because `SIZES` was a module constant.
      productName: l.product_name || null,
      sku: l.sku || null,
      imageUrl: l.image_url || null,
      availableColors: l.available_colors || [],
      availableSizes: l.available_sizes || [],
      // WHICH LOGO this line carries. The shopper's own pick, frozen at
      // submit -- it CANNOT be re-derived from (product, colour), because
      // most colour-rows carry more than one eligible logo. `null` when the
      // product is undecorated, which is a value and not an unknown.
      logoId: l.logo_id != null ? l.logo_id : null,
      // What it may be changed to, KEYED BY COLOUR. The eligible set is per
      // (product, colour), so changing the swatch changes the valid logo
      // set with it -- keyed rather than flat is what lets the editor
      // re-filter without a round-trip. Empty object when the product has
      // no eligible logos at all; the editor renders no picker for that.
      availableLogos: l.available_logos || {},
      // Frozen free-item discount for THIS line. Cents on the wire, dollars
      // in the UI, like every other money value here. The column is NOT NULL
      // DEFAULT 0, so an absent key means "not discounted" -- a value, not an
      // unknown, and 0 rather than undefined so `> 0` tests are meaningful.
      discount:
        l.free_item_discount_cents != null
          ? centsToDollars(l.free_item_discount_cents)
          : Number(l.discount || 0),
    })),
  };
}

/**
 * One `batch_release_items` row, as the stranded-orders surface reads it.
 *
 * `chargedCents` comes from the server's ledger sum and is 0, never null,
 * for an item that never charged -- that zero is a VALUE ("nothing was
 * taken"), not an absence, and it is the discriminator that keeps the two
 * meanings of `held_back` apart. A charge-time held_back took no money; a
 * submit-time one means the shopper was charged and the decorator got
 * nothing. Coerced with Number() so a string from the wire still compares.
 *
 * `retryable` and `exhausted` are passed through, NOT re-derived. Both are
 * rules the backend owns -- which states the retry route accepts, and the
 * per-state attempt bounds (3 for a charge, 6 for a submit). A second copy
 * here is a copy that drifts, and the drift shows up as a retry button on a
 * `done` item, which submits a second real order for goods already in
 * production.
 *
 * `context` is the batch-level half of the release-items response
 * (`store_name`, `distributor_name`, `sender_name`). It is copied onto each
 * item because the shopper message for an abandoned order (option B) is built
 * per item, and an item that carries its own facts cannot be rendered against
 * another batch's. Missing values stay "" -- the message builder drops the
 * phrase rather than printing a blank.
 */
export function normalizeReleaseItem(raw = {}, context = {}) {
  return {
    id: raw.id,
    orderId: raw.order_id,
    // The raw number, "" when absent. `orderLabel` below falls back to a
    // synthetic display id, which is fine on screen but is not an order
    // number a shopper would recognize in a message to them.
    orderNumber: raw.order_number || "",
    orderLabel: raw.order_number || displayId("O", raw.order_id),
    state: raw.state,
    attempts: Number(raw.attempts || 0),
    chargedCents: Number(raw.charged_cents || 0),
    chargedAt: raw.charged_at || null,
    chargedAtLabel: raw.charged_at_label || raw.charged_at || "",
    lastError: raw.last_error || "",
    // A plain sentence for the token, served by the API (one place owns the
    // wording). "" when there is none; the list then shows the raw token.
    lastErrorSummary: raw.last_error_summary || "",
    // Booleans defaulted FALSE when absent, and the direction matters for
    // both. An absent `exhausted` reading true would tell an operator the
    // system had given up when it may still be working; an absent
    // `retryable` reading true would offer an action the API refuses.
    exhausted: Boolean(raw.exhausted),
    retryable: Boolean(raw.retryable),
    // DISTINCT FROM `retryable`, and the distinction is the 2026-09-25
    // follow-up. `retryable` means "the drain will pick this up once its
    // backoff is cleared"; `rearmable` means "this item is TERMINAL and a
    // human may put it back in the live set because the thing that exhausted
    // it has been repaired". An exhausted item reports retryable false and
    // rearmable true at the same time -- that pairing is the incident.
    //
    // Defaulted FALSE when absent for the same reason as the two above: an
    // absent flag reading true would offer an action the API then 409s, which
    // reads to an operator as the recovery having FAILED rather than as never
    // having been available.
    rearmable: Boolean(raw.rearmable),
    refundNeeded: Boolean(raw.refund_needed),
    // The facts option B's shopper message is built from.
    shopperFirstName: raw.shopper_first_name || "",
    placedAt: raw.placed_at || null,
    storeName: context.store_name || "",
    distributorName: context.distributor_name || "",
    senderName: context.sender_name || "",
  };
}

export function normalizeBatch(raw = {}) {
  const orders = (raw.orders || []).map(normalizeOrder);
  const routing = normalizeRouting(
    raw.decorator_routing,
    raw.decorator_name || raw.decorator
  );
  return {
    id: raw.name || raw.display_id || displayId("B", raw.id),
    rawId: raw.id,
    // The backend column is `state`; the UI concept is `status`.
    status: raw.state || raw.status || "open",
    // Orders the operator abandoned and has not refunded yet (option A).
    // Served, not derived: the server computes it from the refunds actually
    // recorded. Keeps a RELEASED batch's stranded list on screen until it is
    // zero. Absent reads as 0 -- an older API owes nothing it can report.
    refundsOwed: Number(raw.refunds_owed || 0),
    orders,
    heldBackCount: orders.filter((o) => o.held).length,
    blanks: centsToDollars(raw.blanks_cost_cents),
    // The ONE decorator's name when there is exactly one, else "". Consumers
    // that need to word the other cases read `decoratorRouting`.
    decorator: raw.decorator_name || raw.decorator || "",
    decoratorRouting: routing,
    // For mid-sentence use ("Release 4 orders to ..."): always a phrase.
    decoratorTarget: decoratorTarget(routing),
    // The API sends INSTANTS (`opened_at`, `holding_since`, `released_at`,
    // `attempted_at`); the short spellings after them are the design
    // fixture's pre-formatted values, kept so the fixture fallback in
    // BatchesPanel.load() still renders. These used to read `*_label` /
    // `*_human` keys that no API build has ever sent, so every one of them
    // was "" and the row printed "opened" with nothing after it.
    opened: formatWhen(raw.opened_at) || raw.opened || "",
    // `holding_since` is when the batch CLOSED to new orders.
    closed: formatWhen(raw.holding_since) || raw.closed || "",
    released: formatWhen(raw.released_at) || raw.released || "",
    // #2772: `attempted_at` is when the last release attempt failed
    // (batches.last_release_attempt_at).
    attempted: formatWhen(raw.attempted_at) || raw.attempted || "",
    holdingSince: durationSince(raw.holding_since) || raw.holdingSince || "",
    failure: raw.failure_reason || raw.failure || "",
    // A failing back-office CLOSE (#4665): "retrying" while the automatic
    // retries run, "stopped" once they gave up, null otherwise. SERVED --
    // the server's Retry gate reads the same policy function -- so BatchRow
    // offers Retry/Abandon on a `releasing` batch exactly when the API will
    // accept them. Absent reads null: an older API offers no new action.
    closeRetry: raw.close_retry || null,
    // BACK-OFFICE REGISTRATION -- a DIFFERENT failure from `failure` above.
    // That one means "we tried to send this batch and could not"; these
    // mean "this batch was never registered with the back office", so it
    // cannot be sent at all. Separate copy and a separate retry endpoint.
    //
    // `openFailed` is the TERMINAL flag: six automatic attempts are spent
    // and nothing will retry without a human. Coerced with Boolean() rather
    // than `||` because a missing key must read false, and `open_attempts`
    // uses `?? 0` so a legitimate 0 is not confused with absent.
    openFailed: Boolean(raw.open_failed),
    openAttempts: raw.open_attempts ?? 0,
    // Defaults TRUE when absent: a batch the API did not describe is
    // assumed healthy. The opposite default would light the failure banner
    // on every row served by an older API build.
    backOfficeRegistered: raw.back_office_registered ?? true,
    // #475. The distributor's name and description -- plain text, rendered
    // with interpolation only. NOT `id` above: `raw.name` is the legacy
    // UI-id column, and a typed name must never become a row's selector.
    displayName: raw.display_name || "",
    description: raw.description || "",
    // What the back office holds (or will hold) as this batch's notes, as
    // the SERVER composes it (services/batch_naming.py). Not re-derived here,
    // so the preview cannot drift from what is sent.
    backOfficeNotes: raw.back_office_notes || "",
    // The batch number the back office ACCEPTED on open ("B-23"), or "" when
    // it holds none. Never the number we asked for.
    backOfficeExternalId: raw.back_office_external_id || "",
    // Served: true while a back-office close is still ahead. Absent reads
    // FALSE -- offering an edit the API would 409 is worse than hiding it.
    detailsEditable: Boolean(raw.details_editable),
    releaseRuleFired: raw.release_rule_fired || null,
    // Served by a SEPARATE call (`listReleaseItems`) and merged in by the
    // panel, so an absent key here means "not fetched yet", not "none". The
    // row must still render the blocked banner and the abandon control in
    // that case: the batch IS blocked, `state` says so, and hiding the only
    // way out until a secondary fetch lands would strand the operator too.
    strandedItems: (raw.release_items || []).map((item) =>
      normalizeReleaseItem(item, raw)
    ),
    // `trigger` is the design fixture's shape. The API sends the window's
    // end as `releases_at` (lifted off `release_trigger` by _batch_payload),
    // NULL when there is no timer: the time rule is off, or a permanent
    // store's batch has no first order yet. Both labels are "" then, and the
    // row renders no clock line rather than "Releases  ·  left".
    trigger: raw.trigger || {
      kind: "window",
      dueLabel: formatWhen(raw.releases_at),
      remaining: durationUntil(raw.releases_at),
    },
  };
}

export function normalizeRules(raw = {}) {
  // NULL means inherit the platform default (design doc 3.2).
  const hours = raw.batch_hold_hours;
  const cents = raw.batch_blanks_threshold_cents;
  return {
    windowHours: hours == null ? DEFAULT_RULES.windowHours : Number(hours),
    threshold: cents == null ? DEFAULT_RULES.threshold : centsToDollars(cents),
    shopperEditing: Boolean(raw.shopper_editing_enabled),
    releaseMode: raw.release_mode || DEFAULT_RULES.releaseMode,
    storeType: raw.store_type || DEFAULT_RULES.storeType,
    // Whether each automatic-closing rule is ON for this store
    // (`batch_rule_toggles_001`). Both columns are NOT NULL DEFAULT TRUE, so
    // `!== false` is the read rather than `Boolean(...)`: an ABSENT key means
    // an API build that predates the columns, and defaulting that to `false`
    // would show every distributor that their batches never close.
    timeRuleEnabled: raw.batch_time_rule_enabled !== false,
    dollarRuleEnabled: raw.batch_dollar_rule_enabled !== false,
  };
}

export function denormalizeRules(rules = {}) {
  const out = {};
  if (rules.windowHours != null) out.batch_hold_hours = Number(rules.windowHours);
  if (rules.threshold != null) out.batch_blanks_threshold_cents = dollarsToCents(rules.threshold);
  if (rules.shopperEditing != null) out.shopper_editing_enabled = Boolean(rules.shopperEditing);
  if (rules.releaseMode != null) out.release_mode = rules.releaseMode;
  // Same `!= null` guard as the rest: a key absent from the patch stays absent
  // on the wire, so a partial save cannot re-enable a rule the distributor
  // turned off. The backend's coercer rejects anything that is not a real
  // boolean, so the Boolean() cast is what keeps a stray "" or 0 from 400ing
  // a save the distributor did make.
  if (rules.timeRuleEnabled != null) {
    out.batch_time_rule_enabled = Boolean(rules.timeRuleEnabled);
  }
  if (rules.dollarRuleEnabled != null) {
    out.batch_dollar_rule_enabled = Boolean(rules.dollarRuleEnabled);
  }
  return out;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function listBatches(storeId, { page = 1, perPage = 8 } = {}) {
  const res = await api.get(`${BASE}/stores/${storeId}/batches`, {
    params: { page, per_page: perPage },
  });
  const data = res.data || {};
  return {
    batches: (data.batches || []).map(normalizeBatch),
    hasMore: Boolean(data.has_more),
  };
}

export async function getStoreBatchRules(storeId) {
  const res = await api.get(`${BASE}/stores/${storeId}/rules`);
  return normalizeRules(res.data || {});
}

export async function updateStoreBatchRules(storeId, rules) {
  const res = await api.patch(`${BASE}/stores/${storeId}/rules`, denormalizeRules(rules));
  return normalizeRules(res.data || {});
}

export async function releaseBatch(batchId) {
  const res = await api.post(`${BASE}/batches/${toNumericId(batchId)}/release`, {});
  const data = res.data || {};
  const batch = normalizeBatch(data.batch || {});
  return {
    batch,
    held: batch.orders.filter((o) => o.held),
    // ADR 0066 consequence: the destination batch id comes back with the
    // release so the result screen can name it.
    nextBatchId: displayId("B", data.held_back_moved_to_batch_id) || null,
  };
}

/**
 * How far along a releasing batch is.
 *
 * Release stopped being synchronous: `releaseBatch` now returns 202 and the
 * batch goes to `releasing`, so "how far along is it?" -- which used to be
 * answered by the release response itself -- is a separate question with its
 * own endpoint (`ui_release_progress`, batch_management.py:2182).
 *
 * TWO NUMBERS, NOT ONE, and a progress bar needs both. `done` is what
 * SHIPPED; `resolved` is what the drain will not touch again. A batch that
 * has entirely finished with some orders held back has `resolved` at 100%
 * and `done` below it -- reporting only `done` leaves a bar stuck short on a
 * batch that is finished, which reads as a hang.
 *
 * The counts are passed through as-is. The endpoint fills EVERY item state,
 * even at zero, precisely so a client never has to tell absent from none --
 * so re-deriving or defaulting them here would throw away the guarantee.
 */
export async function getReleaseProgress(batchId) {
  const res = await api.get(
    `${BASE}/batches/${toNumericId(batchId)}/release-progress`
  );
  const data = res.data || {};
  return {
    batchId: displayId("B", data.batch_id),
    rawId: data.batch_id,
    // The batch's SERVER state. The release control reads this, not a
    // component flag -- see BatchRow's `releaseInFlight`.
    state: data.state || "",
    releasePhase: data.release_phase || null,
    itemCount: Number(data.release_item_count) || 0,
    startedAt: data.release_started_at || null,
    counts: data.counts || {},
    done: Number(data.done) || 0,
    resolved: Number(data.resolved) || 0,
  };
}

export async function closeBatchToNewOrders(batchId) {
  const res = await api.post(`${BASE}/batches/${toNumericId(batchId)}/close`, {});
  return normalizeBatch((res.data || {}).batch || {});
}

/**
 * Save a batch's name and/or description (#475). Sends only the keys given;
 * "" clears a field. Returns the server's re-read batch for `replaceBatch`.
 */
export async function updateBatchDetails(batch, { displayName, description } = {}) {
  const body = {};
  if (displayName !== undefined) body.display_name = displayName;
  if (description !== undefined) body.description = description;
  const res = await api.patch(`${BASE}/batches/${toNumericId(batch)}`, body);
  return normalizeBatch((res.data || {}).batch || {});
}

export async function retryRelease(batchId) {
  const res = await api.post(`${BASE}/batches/${toNumericId(batchId)}/retry`, {});
  return normalizeBatch((res.data || {}).batch || {});
}

/**
 * Restart back-office registration for a batch that gave up.
 *
 * DIFFERENT ENDPOINT from `retryRelease`: that retries SENDING a registered
 * batch, this retries REGISTERING one that never was. A batch with no
 * back-office id cannot be released at all, so this has to succeed first.
 */
export async function retryOpen(batchId) {
  const res = await api.post(
    `${BASE}/batches/${toNumericId(batchId)}/retry-open`,
    {}
  );
  return normalizeBatch((res.data || {}).batch || {});
}

/**
 * PATCH the edited lines of one order.
 *
 * The body carries ONLY the keys the endpoint accepts. It refuses unknown
 * fields BY NAME ("fields not editable in a batch: [...]") and the refusal
 * is all-or-nothing, so one stray key -- a product name, an image URL --
 * 400s the whole order rather than being ignored.
 *
 * `id` is REQUIRED and was missing before: the handler verifies each line id
 * belongs to this order and rejects an idless line outright, which means no
 * save from this editor could ever have applied.
 */
export async function updateOrderLines(orderId, lines) {
  const res = await api.patch(`${BASE}/orders/${toNumericId(orderId)}`, {
    lines: lines.map((l) => ({
      id: l.id,
      style_id: l.styleId,
      color: l.color,
      size: l.size,
      quantity: l.qty,
      // The chosen logo, sent on EVERY line including untouched ones --
      // this is a whole-line PATCH, so the field is always present.
      //
      // The backend validates it only when it actually MOVED, compared
      // against the post-edit style and colour. That scope is what makes
      // resending safe: it used to validate whatever it was handed, so a
      // single line whose STORED artwork is ineligible for its own colour
      // made the WHOLE order unsaveable -- a quantity edit on a different
      // product 403'd (order 270). A logo resent beside a colour change
      // is still a real change and is still validated.
      //
      // The claim this comment previously made -- "sending it is safe
      // because the backend validates it" -- was the assumption that
      // broke. Validation was not the thing that made it safe.
      logo_id: l.logoId,
    })),
  });
  return normalizeOrder((res.data || {}).order || {});
}

/**
 * The per-item stranded detail for one batch.
 *
 * SEPARATE from `listBatches` deliberately: it is only meaningful for a
 * blocked or releasing batch, and joining it into the list payload would
 * make every page of every batch list pay for a join that almost no row
 * needs.
 */
export async function listReleaseItems(batchId) {
  return (await getReleaseOutcome(batchId)).items;
}

/**
 * What a finished (or blocked) release actually did, per order.
 *
 * `items` are the STRANDED ones (the server drops delivered items), and
 * `deliveredOrderIds` are the orders whose item is `done` -- served by the
 * same endpoint because "sent" cannot be read off the stranded list: an order
 * with no release item and an order whose item is done look identical there.
 * The release result screen needs the difference to count sent / held back /
 * not sent truthfully (#424).
 *
 * PASS THE BATCH OBJECT, not `batch.id`: for a named batch `id` is the name
 * ("seed-1-open"), which `toNumericId` cannot resolve (it requested
 * /batches/null/release-items).
 */
export async function getReleaseOutcome(batchId) {
  const res = await api.get(
    `${BASE}/batches/${toNumericId(batchId)}/release-items`
  );
  const data = res.data || {};
  return {
    items: (data.release_items || []).map((raw) =>
      normalizeReleaseItem(raw, data)
    ),
    deliveredOrderIds: (data.delivered_order_ids || []).map(Number),
  };
}

/**
 * The design PREFLIGHT report for one batch (card #643).
 *
 * Which orders a release would hold back, or that the back office already
 * holds without a design, and why -- per order and per item. The sentences
 * (`message`) are the SERVER's: one place owns the wording, so this layer
 * copies them through and never rebuilds one from the parts.
 *
 * `ready` is NOT trusted when orders are listed: a body that omitted it must
 * not make a held order look fine, so it is true only when nothing is listed.
 * A missing body reads as ready-with-nothing, because "no report" must show
 * nothing rather than an invented problem.
 */
export function normalizePreflight(raw) {
  const body = raw || {};
  const orders = (body.orders || []).map((o) => ({
    orderId: o.order_id,
    orderNumber: o.order_number || "",
    purchaseOrder: o.purchase_order || "",
    // Already at the back office (the batch-48 shape), vs held back unsent.
    submitted: Boolean(o.submitted),
    charged: Boolean(o.charged),
    items: (o.items || []).map((i) => ({
      artworkId: i.artwork_id ?? null,
      artworkName: i.artwork_name ?? null,
      placement: i.placement ?? null,
      product: i.product ?? null,
      reason: i.reason || "",
      retryable: Boolean(i.retryable),
      action: i.action || "none",
      message: i.message || "",
    })),
  }));
  return {
    batchId: body.batch_id ?? null,
    liveBackOffice: Boolean(body.live_back_office),
    ready: orders.length === 0,
    orders,
  };
}

/**
 * Read the preflight for a batch. READ-ONLY and no external call on the
 * server, so it is safe to load whenever a row is opened.
 *
 * PASS THE BATCH OBJECT, not `batch.id` (see `getReleaseOutcome`).
 */
export async function getBatchPreflight(batchId) {
  const res = await api.get(
    `${BASE}/batches/${toNumericId(batchId)}/preflight`
  );
  return normalizePreflight(res.data);
}

/**
 * Re-send ONE already-submitted order to the back office, in place (card #643).
 *
 * DRY RUN BY DEFAULT: without `confirm` the server sends nothing and answers
 * with the redacted body it WOULD send. Only the JSON boolean `true` sends
 * (`{confirm: true}`), so a truthy non-boolean can never reach the wire.
 *
 * A 409 (refused here) and a 502 (partner refusal) carry the outcome body, so
 * they are ANSWERS, returned with `httpStatus`; the caller words the `result`
 * token. An error with no outcome body (403/404 scope, 429, a network failure)
 * is re-thrown for the caller to word by status.
 */
export async function resendOrder(orderId, { confirm = false } = {}) {
  const id = toNumericId(orderId);
  const body = confirm === true ? { confirm: true } : {};
  let res;
  try {
    res = await api.post(`${BASE}/orders/${id}/resend`, body);
  } catch (err) {
    const r = err && err.response;
    if (r && r.data && typeof r.data.result === "string") {
      return normalizeResend(r.data, r.status);
    }
    throw err;
  }
  return normalizeResend(res.data, res.status ?? 200);
}

export function normalizeResend(raw, httpStatus = 200) {
  const body = raw || {};
  const payload = body.payload || null;
  return {
    orderId: body.order_id ?? null,
    batchId: body.batch_id ?? null,
    result: body.result || "",
    dryRun: Boolean(body.dry_run),
    sent: Boolean(body.sent),
    // null = unknown (the server could not tell); never read as true.
    applied: body.applied === true ? true : body.applied === null ? null : false,
    mode: body.mode || null,
    designsMissing: Array.isArray(body.designs_missing) ? body.designs_missing : [],
    // What the dry run would send: the lines only, as plain strings.
    lineItems: payload && Array.isArray(payload.line_items)
      ? payload.line_items.map((li) => ({
          productCode: String((li && li.product_code) || ""),
          color: String((li && li.color) || ""),
          description: String((li && li.description) || ""),
        }))
      : [],
    httpStatus,
  };
}

/**
 * Re-arm ONE stranded item: clears its attempt counter and its backoff.
 *
 * Keyed on the RELEASE-ITEM id, not the order id -- a distinction the caller
 * has to get right, because an order id here reaches a different item or
 * none. The state is deliberately NOT changed by the server: a charged item
 * still needs submitting, not re-charging.
 */
export async function retryReleaseItem(itemId) {
  const res = await api.post(`${BASE}/release-items/${itemId}/retry`, {});
  return (res.data || {}).release_item || null;
}

/**
 * Re-arm ONE exhausted item, so a repaired cause can be tried again.
 *
 * DISTINCT FROM `retryReleaseItem`, which nudges an item that is already live
 * and explicitly does NOT change its state. This one moves a TERMINAL item
 * back into the live set, which is why it is a separate route with its own
 * refusals rather than a flag on the other.
 *
 * THE SERVER DECIDES WHICH STATE IT RETURNS TO -- `charged` when money moved,
 * `pending` when it did not -- read from the union of the item's `charged_at`
 * and the `order_charge` ledger. Nothing here may guess at that: sending a
 * paid order back to `pending` would put it in front of the charger.
 *
 * It does not classify the cause either. #3807 verifies the back-office batch
 * id at submit time, so a repaired cause proves itself by succeeding and a
 * genuine failure goes terminal again.
 */
export async function rearmReleaseItem(itemId) {
  const res = await api.post(`${BASE}/release-items/${itemId}/rearm`, {});
  return (res.data || {}).release_item || null;
}

/**
 * Give up on a batch's unresolved orders, close it, and stamp it released.
 *
 * DESTRUCTIVE AND IRREVERSIBLE from the UI's point of view, and it DOES NOT
 * REFUND. Jake, 2026-09-15: an automatic refund is the trust-buster. The
 * response carries `refund_needed_orders` -- the orders a human now owes
 * money back on -- which is what this call gives the operator INSTEAD of
 * moving the money.
 */
export async function abandonRelease(batchId) {
  const res = await api.post(
    `${BASE}/batches/${toNumericId(batchId)}/abandon`,
    {}
  );
  const data = res.data || {};
  // NORMALIZED like every other call that hands the panel a batch to swap
  // in. `replaceBatch` matches on the normalized id ("B-77"); the raw batch
  // carries the numeric one, matched nothing, and the row kept showing the
  // pre-abandon state until a reload.
  return { ...data, batch: data.batch ? normalizeBatch(data.batch) : null };
}

export async function cancelOrder(orderId) {
  await api.post(`${BASE}/orders/${toNumericId(orderId)}/cancel`, {});
}

/**
 * Move orders from `sourceBatch` into `destinationBatch` (same store, both
 * open or holding). All or nothing: the server moves every order or none.
 *
 * Takes NORMALIZED objects and sends their primary keys through
 * `toNumericId`, because the endpoint accepts integers only -- a display id
 * or a seeded batch name ("seed-1-holding") is a 400 by design.
 *
 * `dryRun` asks the server to validate and report the before/after rollups
 * without writing anything. Returns the response body as served:
 * `{dry_run, moved_order_ids, source, destination, orders}`.
 */
export async function moveOrdersToBatch(
  sourceBatch,
  orders,
  destinationBatch,
  { dryRun = false } = {}
) {
  const res = await api.post(
    `${BASE}/batches/${toNumericId(sourceBatch)}/move-orders`,
    {
      order_ids: orders.map((o) => toNumericId(o)),
      destination_batch_id: toNumericId(destinationBatch),
      dry_run: Boolean(dryRun),
    }
  );
  return res.data || {};
}

// ---------------------------------------------------------------------------
// Fixtures.
//
// The batch-management endpoints are being built by a parallel worker. Until
// they land the panel renders from these, so the surface is reviewable and
// the ui-runner scenarios have deterministic ids to anchor on. They mirror
// design/bm-data.jsx. Each call returns a FRESH deep copy so one component
// mutating an order cannot poison another's fixture.
// ---------------------------------------------------------------------------

const DECORATOR = "Midwest Stitch";

let fixtureLineSeq = 0;

/**
 * One fixture line, in the shape `normalizeOrder` now produces.
 *
 * The product facts ride on the LINE, because that is where the API puts
 * them -- `availableColors` / `availableSizes` per line, sourced from the
 * campaign product. The editor reads only the line, so a fixture that
 * omitted them would render a card with no swatches and no size rows.
 *
 * `STYLES` is invented, and that is fine HERE and only here: this dataset is
 * explicitly fake and opt-in (`demoFixtures`), never on a surface a
 * distributor reaches. What was wrong was the editor reaching into it for
 * REAL orders.
 */
const fx = (styleId, color, size, qty, decoration) => {
  const st = STYLES.find((s) => s.id === styleId);
  return {
    id: 9000 + ++fixtureLineSeq,
    styleId,
    style: st.name,
    productName: st.name,
    sku: String(styleId).toUpperCase(),
    imageUrl: null,
    base: st.base,
    color,
    size,
    qty,
    decoration,
    availableColors: st.colors.map((c) => ({
      id: c,
      name: c,
      hex: COLOR_HEX[c] || null,
    })),
    // The fixture's own flat price, for every size. A REAL line gets
    // `available_sizes` from the API, where `price_cents` is the
    // size-inclusive charged price; this fake dataset has no per-size prices
    // to be inclusive OF, so inventing a spread here would only re-create
    // the constant this file just lost (GH #3741). Nothing foots from this
    // field -- the editor reads `id`/`label` to build its size rows, and
    // totals come from the line's `base`, which is `st.base` above. Keeping
    // the two equal is what stops the fixture implying a price the fixture
    // does not charge.
    availableSizes: SIZES.map((s) => ({
      id: s,
      label: s,
      price_cents: dollarsToCents(st.base),
    })),
  };
};

let fixtureOrderSeq = 0;
const fxOrder = (shopper, dept, lines, extra = {}) => ({
  id: "O-" + (7200 + ++fixtureOrderSeq),
  shopper,
  dept,
  lines,
  placed: extra.placed || "Aug 20, 8:41 AM",
  paymentState: "pending_capture",
  held: false,
  ...extra,
});

const NAMES = [
  "Jonah Reyes", "Kim Lindqvist", "Luis Ortega", "Maya Bhatt", "Nate Cullen",
  "Oona Selby", "Pete Dvorak", "Quinn Ashby", "Rosa Delgado", "Sam Whitcomb",
  "Tessa Nolan", "Umar Siddiqui", "Vera Kaminski", "Wes Hollins",
  "Xochitl Mena", "Yara Osei",
];

function buildFixtures() {
  fixtureOrderSeq = 0;
  // Reset alongside the order counter so repeated calls are deterministic --
  // the ui-runner scenarios anchor on these ids.
  fixtureLineSeq = 0;

  const openOrders = [
    fxOrder("Dana Whitfield", "Facilities", [fx("p1", "Navy", "L", 2, "Left chest logo")], { placed: "Aug 20, 9:12 AM" }),
    fxOrder("Marcus Bell", "Sales", [fx("p2", "Black", "2XL", 1, "Left chest logo"), fx("p3", "White", "2XL", 2, "Full front")], { placed: "Aug 20, 9:48 AM" }),
    fxOrder("Priya Raman", "Engineering", [fx("p3", "Navy Frost", "M", 3, "Full front")], { placed: "Aug 20, 10:20 AM" }),
    fxOrder("Tom Okafor", "Facilities", [fx("p4", "Navy", "XL", 1, "Left chest + nape")], { placed: "Aug 20, 11:02 AM" }),
  ];

  const holdingOrders = [
    fxOrder("Alicia Fuentes", "Sales", [fx("p1", "White", "S", 2, "Left chest logo")], { placed: "Aug 18, 2:15 PM" }),
    fxOrder("Ben Corliss", "Engineering", [fx("p2", "Navy", "L", 1, "Left chest logo")], { placed: "Aug 18, 4:02 PM" }),
    fxOrder("Cheryl Ndiaye", "Facilities", [fx("p3", "Black Frost", "3XL", 4, "Full front")], { placed: "Aug 19, 8:30 AM" }),
    fxOrder("Devon Park", "Sales", [fx("p4", "Black", "M", 1, "Left chest + nape"), fx("p1", "Navy", "M", 1, "Left chest logo")], { placed: "Aug 19, 9:44 AM" }),
    fxOrder("Erika Sloan", "Marketing", [fx("p1", "Steel Grey", "XL", 3, "Left chest logo")], { placed: "Aug 19, 1:12 PM" }),
    fxOrder("Farid Hassan", "Engineering", [fx("p3", "White", "L", 2, "Full front")], { placed: "Aug 19, 3:50 PM" }),
    fxOrder("Grace Lieu", "Marketing", [fx("p2", "White", "S", 1, "Left chest logo")], { placed: "Aug 19, 5:19 PM" }),
    fxOrder("Hugo Marchetti", "Facilities", [fx("p4", "Carhartt Brown", "2XL", 2, "Left chest + nape")], { placed: "Aug 20, 7:05 AM" }),
    fxOrder("Iris Tanaka", "Sales", [fx("p1", "Royal", "M", 2, "Left chest logo")], { placed: "Aug 20, 7:58 AM" }),
  ];

  const releasedOrders = (n, seedNames) =>
    seedNames.slice(0, n).map((nm, i) =>
      fxOrder(
        nm,
        ["Sales", "Engineering", "Facilities", "Marketing"][i % 4],
        [fx(STYLES[i % 4].id, ["Navy", "Black", "White", "Navy Frost"][i % 4], ["M", "L", "XL", "2XL"][i % 4], (i % 3) + 1, "Left chest logo")],
        { placed: "Aug 1" + (i % 8) + ", " + (8 + (i % 9)) + ":1" + (i % 9) + " AM", paymentState: "charged" }
      )
    );

  // B-1045 released with one card declined. The order was not charged and
  // rolled forward into the batch that was open at the time.
  const heldBackOrders = releasedOrders(12, NAMES);
  heldBackOrders[4] = {
    ...heldBackOrders[4],
    held: true,
    paymentState: "charge_failed",
    reason: "Card declined -- insufficient funds",
    movedTo: "B-1047",
  };

  const batches = [
    {
      id: "B-1047", rawId: 1047, status: "open", orders: openOrders,
      opened: "Aug 20, 9:12 AM", blanks: 186.0, decorator: DECORATOR, heldBackCount: 0,
      trigger: { kind: "window", hours: 48, dueLabel: "Aug 22, 9:12 AM", remaining: "21h 47m" },
    },
    {
      id: "B-1046", rawId: 1046, status: "holding", orders: holdingOrders,
      opened: "Aug 18, 2:15 PM", closed: "Aug 20, 8:00 AM", blanks: 512.4,
      decorator: DECORATOR, holdingSince: "4h 12m", heldBackCount: 0, trigger: {},
    },
    {
      id: "B-1045", rawId: 1045, status: "released", orders: heldBackOrders,
      opened: "Aug 16, 7:40 AM", released: "Aug 18, 8:00 AM", blanks: 690.0,
      decorator: DECORATOR, heldBackCount: 1, trigger: {},
    },
    {
      id: "B-1044", rawId: 1044, status: "failed", orders: releasedOrders(7, NAMES.slice(3)),
      opened: "Aug 14, 9:02 AM", attempted: "Aug 16, 8:00 AM", blanks: 402.15,
      decorator: DECORATOR, heldBackCount: 0, trigger: {},
      failure: "The decorator rejected the batch: 2 items reference a decoration file that is no longer approved.",
    },
  ];

  // A standing store accumulates a batch every day or two, indefinitely --
  // enough history to exercise the paged list.
  for (let i = 0; i < 26; i++) {
    const n = 1043 - i;
    batches.push({
      id: "B-" + n, rawId: n, status: "released",
      orders: releasedOrders(4 + ((i * 3) % 9), NAMES.slice(i % 6)),
      opened: "Jul " + Math.max(1, 30 - i) + ", 7:15 AM",
      released: "Aug " + Math.max(1, 12 - i) + ", 8:00 AM",
      blanks: 220 + ((i * 47) % 600), decorator: DECORATOR,
      heldBackCount: 0, trigger: {},
    });
  }

  return batches;
}

export function makeFixtureBatches() {
  // Deep copy so callers cannot mutate a shared fixture. The fixtures are
  // written pre-normalized with a bare `decorator`, so the routing fields
  // normalizeBatch would derive are added here.
  return JSON.parse(JSON.stringify(buildFixtures())).map((b) => {
    const routing = normalizeRouting(null, b.decorator);
    return { ...b, decoratorRouting: routing, decoratorTarget: decoratorTarget(routing) };
  });
}
